import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, Chip, Tooltip } from '@mui/material';
import LogTheme from './LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import { getAuthHeaders } from '../../utils/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const Invitations = () => {
  const { userId, userRole } = useAuth();
  const { logbooks } = useLogbook();
  const ownedLogbooks = (logbooks || []).filter((lb) => lb.isOwner);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPremium, setHasPremium] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newInvitation, setNewInvitation] = useState({ invitedEmail: '', role: 'reader', logbookId: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);

  useEffect(() => {
    if (!userId) {
      setSubscriptionLoading(false);
      return;
    }
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/status`, { headers: getAuthHeaders() })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        setHasPremium(!!(data && data.hasActiveOrPendingPremium));
      })
      .catch(() => setHasPremium(false))
      .finally(() => setSubscriptionLoading(false));
  }, [userId]);

  useEffect(() => {
    if (userId) fetchInvitations();
  }, [userId]);

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/invitations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!newInvitation.invitedEmail) {
      setError('E-postadress krävs.');
      return;
    }
    if (!newInvitation.logbookId) {
      setError('Välj en loggbok.');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/invitations`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invitedEmail: newInvitation.invitedEmail,
          role: newInvitation.role,
          logbookId: newInvitation.logbookId
        })
      });

      if (response.ok) {
        setSuccess('Inbjudan skapad!');
        setOpenDialog(false);
        setNewInvitation({ invitedEmail: '', role: 'reader', logbookId: ownedLogbooks[0]?._id || '' });
        fetchInvitations();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fel vid skapande av inbjudan.');
      }
    } catch (error) {
      setError('Något gick fel.');
    }
  };

  const handleChangeRole = async (sharedAccessId, newRole) => {
    if (!sharedAccessId || !['reader', 'editor'].includes(newRole)) return;
    setUpdatingRoleId(sharedAccessId);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/invitations/shared-access/${sharedAccessId}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        setSuccess('Behörighet uppdaterad.');
        fetchInvitations();
      } else {
        const data = await response.json();
        setError(data.message || 'Kunde inte uppdatera behörighet.');
      }
    } catch (err) {
      setError('Något gick fel.');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleRevokeInvitation = async (invitationId, { accepted } = {}) => {
    const message = accepted
      ? 'Personen förlorar åtkomst till din loggbok. Användarkontot och övriga data på plattformen påverkas inte. Vill du fortsätta?'
      : 'Är du säker på att du vill återkalla denna inbjudan? Mottagaren kan inte längre använda inbjudningskoden.';
    if (!window.confirm(message)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSuccess(accepted ? 'Åtkomst till loggboken har tagits bort.' : 'Inbjudan återkallad.');
        fetchInvitations();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Fel vid återkallande av inbjudan.');
      }
    } catch (error) {
      setError('Något gick fel.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Kopierat till urklipp!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'revoked': return 'error';
      default: return 'default';
    }
  };

  const hasEffectivePremium = hasPremium || userRole === 'superadmin';
  const canCreateInvitations = hasEffectivePremium && ownedLogbooks.length > 0;

  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h1">Inbjudningar</Typography>
              {!subscriptionLoading && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  disabled={!hasEffectivePremium}
                  onClick={() => {
                    setOpenDialog(true);
                    setNewInvitation((prev) => ({ ...prev, logbookId: prev.logbookId || (ownedLogbooks[0] && ownedLogbooks[0]._id) || '' }));
                  }}
                  sx={{
                    minWidth: { xs: 0, sm: 'auto' },
                    '& .MuiButton-startIcon': { marginRight: { xs: 0, sm: 1 } }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Skapa inbjudan
                  </Box>
                </Button>
              )}
            </Box>

            {!subscriptionLoading && !hasEffectivePremium && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Endast Premium-prenumerationer kan bjuda in andra användare. Gå till{' '}
                <Button component={Link} to="/subscription" size="small" variant="outlined">
                  Prenumerationsplaner
                </Button>{' '}
                för att uppgradera.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>E-post</TableCell>
                    <TableCell>Roll</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Inbjudningskod</TableCell>
                    <TableCell>Skapad</TableCell>
                    <TableCell>Åtgärder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => {
                    const isAcceptedWithAccess = invitation.status === 'accepted' && invitation.sharedAccessId;
                    const displayRole = isAcceptedWithAccess ? (invitation.currentRole || invitation.role) : invitation.role;
                    return (
                      <TableRow key={invitation._id}>
                        <TableCell>{invitation.invitedEmail}</TableCell>
                        <TableCell>
                          {isAcceptedWithAccess ? (
                            <FormControl size="small" sx={{ minWidth: 140 }} disabled={updatingRoleId === invitation.sharedAccessId}>
                              <Select
                                value={displayRole}
                                onChange={(e) => handleChangeRole(invitation.sharedAccessId, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="reader">Läsare</MenuItem>
                                <MenuItem value="editor">Redaktör</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip label={displayRole === 'reader' ? 'Läsare' : 'Redaktör'} size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.status === 'pending' ? 'Väntar' : invitation.status === 'accepted' ? 'Accepterad' : 'Återkallad'}
                            color={getStatusColor(invitation.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {invitation.invitationCode ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {invitation.invitationCode.substring(0, 8)}...
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(invitation.invitationCode)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(invitation.createdAt).toLocaleDateString('sv-SE')}
                        </TableCell>
                        <TableCell>
                          {invitation.status === 'pending' && (
                            <Tooltip title="Återkalla inbjudan">
                              <IconButton
                                color="error"
                                aria-label="Återkalla inbjudan"
                                onClick={() => handleRevokeInvitation(invitation._id, { accepted: false })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invitation.status === 'accepted' && (
                            <Tooltip title="Ta bort åtkomst till loggbok (raderar inte användarens konto)">
                              <IconButton
                                color="error"
                                aria-label="Ta bort åtkomst till loggbok"
                                onClick={() => handleRevokeInvitation(invitation._id, { accepted: true })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Skapa ny inbjudan</DialogTitle>
          <DialogContent>
            {ownedLogbooks.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Du behöver äga en loggbok för att bjuda in andra. Skapa en loggbok på startsidan.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                  <InputLabel>Loggbok</InputLabel>
                  <Select
                    value={newInvitation.logbookId}
                    label="Loggbok"
                    onChange={(e) => setNewInvitation({ ...newInvitation, logbookId: e.target.value })}
                  >
                    {ownedLogbooks.map((lb) => (
                      <MenuItem key={lb._id} value={lb._id}>
                        {lb.name || 'Min loggbok'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="E-postadress"
                  type="email"
                  value={newInvitation.invitedEmail}
                  onChange={(e) => setNewInvitation({ ...newInvitation, invitedEmail: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel>Roll</InputLabel>
                  <Select
                    value={newInvitation.role}
                    label="Roll"
                    onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                  >
                    <MenuItem value="reader">Läsare (kan endast se)</MenuItem>
                    <MenuItem value="editor">Redaktör (kan se och ändra)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 2 }}>
                  Inbjudna får först läsbehörighet som standard. Du kan ge redigeringsrätt när som helst här på inbjudningslistan efter att de accepterat.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Avbryt</Button>
            <Button onClick={handleCreateInvitation} variant="contained" disabled={!canCreateInvitations}>Skapa</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LogTheme>
  );
};

export default Invitations;



